import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { TrainIdentifierWithIncidentIdString } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/utils";
import { AffectedTrain as AffectedTrainType } from "features/CenterContent/RoleContent/Vaktleder/types";
import { findSeriesDiscrepancies } from "features/History/groupTrainSeries";
import { TrainFilter } from "features/VehicleSidebar/AffectedTrains/TrainFilter";
import { TrainRouteDiscrepancyWarning } from "features/VehicleSidebar/AffectedTrains/TrainRouteDiscrepancyWarning";
import AffectedTrain from "features/VehicleSidebar/AffectedTrains/lists/UnhandledListElement";
import { ButtonGroupFitter } from "features/VehicleSidebar/AffectedTrains/styles";
import {
  AffectedTrainFilter,
  TrainRouteDiscrepancies,
} from "features/VehicleSidebar/AffectedTrains/types";
import { sortByIncidentArrivalTime } from "features/VehicleSidebar/AffectedTrains/utils/AffectedTrainSorter";
import { getFilteredTrains } from "features/VehicleSidebar/AffectedTrains/utils/filterAffectedTrains";
import { useMinute } from "features/VehicleSidebar/AffectedTrains/utils/timeFormatting";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  DropsCheckbox,
  DropsStaticAlert,
  Flex,
  Tooltip,
} from "shared/components/Spor";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { useInfiniteScroll } from "shared/hooks/useInfiniteScroll";

type UnhandledTrainListProps = {
  trains: AffectedTrainType[];
  selectedTrains: AffectedTrainType[];
  updateSelectedTrains: (train: AffectedTrainType) => void;
  clearSelectedTrains: () => void;
  batchUpdatedSelectedTrains: (affectedTrains: AffectedTrainType[]) => void;
  handleCreateEvents: () => void;
  showFilter: boolean;
  filter: AffectedTrainFilter;
  setFilter: (filter: AffectedTrainFilter) => void;
  pendingTrains: TrainIdentifierWithIncidentIdString[];
  refetch: () => void;
  setTabsPulse: (value: number) => void;
  notAffected?: boolean;
};

export const UnhandledTrainList: FC<UnhandledTrainListProps> = ({
  trains,
  selectedTrains,
  updateSelectedTrains,
  clearSelectedTrains,
  batchUpdatedSelectedTrains,
  handleCreateEvents,
  showFilter,
  filter,
  setFilter,
  pendingTrains,
  refetch,
  setTabsPulse,
  notAffected,
}) => {
  const { t } = useTranslation();

  const currentTime = useMinute();

  const [routeDiscrepancies, setRouteDiscrepancies] =
    useState<TrainRouteDiscrepancies>(null);

  useEffect(() => {
    const discrepancies = findSeriesDiscrepancies(selectedTrains);
    setRouteDiscrepancies(
      Object.keys(discrepancies).length > 0 ? discrepancies : null,
    );
  }, [selectedTrains]);

  // Ensure that only trains from the selected incident is included in batch
  useEffect(() => {
    clearSelectedTrains();
  }, [clearSelectedTrains, filter?.incidentId]);

  const selectedTrainsInBatch = useMemo(
    () =>
      selectedTrains.filter(
        (train) => train.incidentId === selectedTrains[0]?.incidentId,
      ),
    [selectedTrains],
  );

  const filteredTrains = useMemo(
    () => getFilteredTrains(filter, trains),
    [filter, trains],
  );

  const selectableFilteredTrains = useMemo(
    () =>
      filteredTrains.filter((train) => {
        const identifier: TrainIdentifierWithIncidentIdString = `${train.incidentId}-${train.trainId.countryCode}-${train.trainId.identifier}-${train.trainId.nominalDate}`;
        return !pendingTrains.includes(identifier);
      }),
    [filteredTrains, pendingTrains],
  );

  const allTrainsSelected =
    selectableFilteredTrains.length > 0 &&
    selectableFilteredTrains.every((train) =>
      selectedTrains.some(
        (selectedTrain) =>
          selectedTrain.incidentId === train.incidentId &&
          selectedTrain.trainId.countryCode === train.trainId.countryCode &&
          selectedTrain.trainId.identifier === train.trainId.identifier &&
          selectedTrain.trainId.nominalDate === train.trainId.nominalDate,
      ),
    );

  const selectAllTrains = useCallback(() => {
    if (selectableFilteredTrains.length === 0) {
      clearSelectedTrains();
      return;
    }

    clearSelectedTrains();
    batchUpdatedSelectedTrains(selectableFilteredTrains);
  }, [
    batchUpdatedSelectedTrains,
    clearSelectedTrains,
    selectableFilteredTrains,
  ]);

  const deselectAllTrains = useCallback(() => {
    clearSelectedTrains();
  }, [clearSelectedTrains]);

  const containerRef = useRef<HTMLDivElement>(null);

  const { numberOfItemsToRender } = useInfiniteScroll({
    containerRef,
    initialItemsToRender: 30,
    maxItemsToRender: filteredTrains.length,
  });

  const [error, setError] = useState(false);
  const markSelectedNotAffectedMutation = useMutation({
    mutationFn: () =>
      mutationFnPOST(
        `${getBackendUrl()}/affected-trains`,
        selectedTrains.map((train) => ({
          ...train,
          state: notAffected ? "AFFECTED" : "NOT_AFFECTED",
        })),
      ),
    onSuccess: () => {
      clearSelectedTrains();
      setError(false);
      refetch();
      (() => (notAffected ? setTabsPulse(1) : setTabsPulse(3)))();
      setTimeout(() => {
        setTabsPulse(0);
      }, 1000);
    },
    onError: () => {
      setError(true);
    },
  });

  const markSelectedNotAffected = () => {
    markSelectedNotAffectedMutation.mutate();
  };

  return (
    <>
      {error && (
        <DropsStaticAlert gridRow="2" gridColumn="span 2" variant="error">
          {t("Kunne ikke markere som ")}
          {notAffected ? t("berørt") : t("ikke berørt")}
        </DropsStaticAlert>
      )}

      <Flex flexDir="column" height="100%">
        <TrainFilter
          trains={trains}
          filter={filter}
          showFilter={showFilter}
          setFilter={setFilter}
          selectedTrains={selectedTrains}
          batchUpdatedSelectedTrains={batchUpdatedSelectedTrains}
        />
        {routeDiscrepancies && (
          <Box p={2}>
            <TrainRouteDiscrepancyWarning
              trainRouteDiscrepancies={routeDiscrepancies}
            />
          </Box>
        )}
        <ButtonGroupFitter>
          {/* TODO: add popover once SPOR issue https://github.com/nsbno/spor/issues/2167 is resolved */}
          <Tooltip
            label={t("Filtrér på strekning eller togserie(r)")}
            placement="right"
            hasArrow
            isDisabled={filter != null}
          >
            <DropsCheckbox
              onCheckedChange={
                allTrainsSelected ? deselectAllTrains : selectAllTrains
              }
              checked={allTrainsSelected}
              ml="12px" // to match other checkboxes
              whiteSpace="nowrap"
              disabled={filter == null}
            >
              {t("Marker alle tog")}
            </DropsCheckbox>
          </Tooltip>
          {/* TODO: add popover once SPOR issue https://github.com/nsbno/spor/issues/2167 is resolved */}
          <Tooltip
            label={t("Marker tog i listen under")}
            placement="top"
            hasArrow
            isDisabled={selectedTrains.length > 0}
          >
            <Flex gap="6px">
              <Button
                size="xs"
                variant={notAffected ? "primary" : "secondary"}
                onClick={markSelectedNotAffected}
                whiteSpace="nowrap"
                height="30px"
                disabled={selectedTrains.length <= 0}
              >
                {notAffected ? (
                  <Flex gap="6px" alignItems="center">
                    {t("Likevel berørt")}
                  </Flex>
                ) : (
                  t("Ikke berørt")
                )}
              </Button>

              <Button
                size="xs"
                fontWeight="600"
                variant="primary"
                onClick={handleCreateEvents}
                disabled={selectedTrains.length <= 0}
              >
                {selectedTrains.length > 0
                  ? t("Behandle {{count}} tog", {
                      count: selectedTrainsInBatch.length,
                    })
                  : t("Behandle tog")}
              </Button>
            </Flex>
          </Tooltip>
        </ButtonGroupFitter>

        <Box
          flex="1"
          overflow="auto"
          ref={containerRef}
          height="100vh"
          p="12px"
        >
          {filteredTrains.length > 0 ? (
            filteredTrains
              .toSorted(sortByIncidentArrivalTime)
              .slice(0, numberOfItemsToRender)
              .map((train) => {
                const identifier: TrainIdentifierWithIncidentIdString = `${train.incidentId}-${train.trainId.countryCode}-${train.trainId.identifier}-${train.trainId.nominalDate}`;

                return (
                  <AffectedTrain
                    train={train}
                    selectedTrains={selectedTrains}
                    updateSelectedTrains={updateSelectedTrains}
                    key={identifier}
                    isPending={pendingTrains.includes(identifier)}
                    currentTime={currentTime}
                  />
                );
              })
          ) : (
            <NoDataMessage
              pt="12px"
              height="100%"
              reason={t("Filteret gir ingen treff")}
            />
          )}
        </Box>
      </Flex>
    </>
  );
};
